<template>
    <div ref="containerRef" class="@container h-full flex assignment-list-view">
        <div class="flex flex-col p-2 pt-0 grow">
            <multi-assignment-creator v-if="MultiCreateFeature.isEnabled()"/>
            <div class="flex gap-2 py-4 items-center">
                <h1 class="text-xl font-bold">Oppgaver</h1>
                <UBadge
                    v-if="numberOfAssignments"
                    variant="outline"
                    color="neutral"
                    :label="numberOfAssignments"
                />
            </div>
            <assignment-file-uploader
                class="max-w-[800px]"
                @upload-success="createAssignments"
            />
            <assignment-header
                @on-status-selected="handleChangeStatus"
                @on-category-selected="handleChangeCategory"
                @on-search-term-updated="handleChangeSearchTerm"
            />
            <ag-grid-vue
                id="assignment-list"
                class="flex-1 min-h-0"
                :theme="agTheme"
                :server-side-datasource="taskDataSource"
                :column-defs="colDefs"
                :grid-options="gridOptions"
            />
        </div>

        <div
            v-if="detailsVisible && selectedUuid"
            class="relative w-1 cursor-col-resize"
            @mousedown.stop.prevent="isResizing = true"
        >
            <div class="absolute inset-y-0 -left-1.5 -right-1.5"/>
        </div>

        <UCard
            v-if="detailsVisible && selectedUuid"
            :style="{ width: `${detailsWidth}px`, minWidth: `${detailsMinWidth}px` }"
            :ui="{
                body: 'py-2 sm:py-2 h-full'
            }"
        >
            <assignment-details
                :assignment-uuid="selectedUuid"
                :grid-api="gridApi"
                @close="detailsVisible = false"
            />
        </UCard>
    </div>
</template>

<script setup lang="ts">
import {AgGridVue} from 'ag-grid-vue3'
import {
    CellFocusedEvent,
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
    ICellRendererParams,
    IRowNode,
    IServerSideDatasource,
    IServerSideGetRowsParams,
    RowDoubleClickedEvent,
} from 'ag-grid-community'
import {toQueryRequest} from '@/query/AgGridAdapter'
import {ASSIGNMENTS_CHANGED_EVENTS, createIncomingDocumentAssignment, detailsVisible, fetchAssignments} from '@/assignments/assignment.service'
import ResourceLinksCellRenderer from '@/resource-link/ResourceLinksCellRenderer.vue'
import {onMounted, onUnmounted, ref, shallowRef} from 'vue'
import {useSingletonEmitter} from '@/services/infrastructure/emitter'
import {ORG_SELECTED, ORG_SWITCHED} from '@/types/events'
import {agTheme} from '@/agUtils/ag-theme.service'
import {Assignment} from '@/assignments/Assignment.type'
import {resolveCellRenderer} from '@/assignments/ActionableCellSelector'
import CategoryCellRenderer from '@/assignments/CategoryCellRenderer.vue'
import StatusCellRenderer from '@/assignments/StatusCellRenderer.vue'
import AssignmentHeader from '@/assignments/AssignmentHeader.vue'
import AssignmentDetails from '@/assignments/details/AssignmentDetails.vue'
import {DocumentMeta} from '@/types/transaction/DocumentMeta'
import AssignmentFileUploader from '@/assignments/AssignmentFileUploader.vue'
import {useElementBounding, useEventListener} from '@vueuse/core'
import {MultiCreateFeature} from '@/subaccount/feature-toggles'
import MultiAssignmentCreator from '@/components/MultiAssignmentCreator.vue'
import emitter from '@/services/infrastructure/emitter'
import {SUB_ACCOUNT_CREATED} from '@/subaccount/subAccount.service'
import EditAssignmentPopup from '@/assignments/EditAssignmentPopup.vue'
import {useOverlay} from '@nuxt/ui/composables/useOverlay'

const gridApi = shallowRef<GridApi<Assignment>>()
const {onEvents, cleanUp} = useSingletonEmitter('Assignments')

emitter.on(SUB_ACCOUNT_CREATED, () => {
    if (gridApi.value) gridApi.value.refreshServerSide()
})

const selectedStatusFilter = ref({
    status: {
        filterType: 'text',
        type: 'equals',
        filter: 'pending'
    },
})

const selectedCategoryFilter = ref()
const searchTerm = ref('')

const containerRef = ref(null)
const isResizing = ref(false)
const {right: containerRight, width: containerTotalWidth} = useElementBounding(containerRef)
const detailsWidth = ref(450)
const detailsMinWidth = 300

const overlay = useOverlay()
const editAssignmentModal = overlay.create(EditAssignmentPopup)

const selectedUuid = ref<string>()
const numberOfAssignments = ref<number | undefined>(undefined)

const taskDataSource: IServerSideDatasource<Assignment> = {
    async getRows(params: IServerSideGetRowsParams<Assignment>) {
        params.request.filterModel = {
            ...selectedStatusFilter.value,
            ...selectedCategoryFilter.value,
            ...params.request.filterModel,
        }

        const assignments: Assignment[] = await fetchAssignments(toQueryRequest(params.request))
        if (assignments) {
            numberOfAssignments.value = assignments.length
            params.success({rowData: assignments})
            return
        }
    }
}

const gridOptions: GridOptions = {
    onGridReady: (event: GridReadyEvent) => {
        gridApi.value = event.api
    },
    getRowId: (params) => {
        return params.data.uuid
    },
    rowModelType: 'serverSide',
    rowHeight: 60,
    defaultColDef: {
        sortable: false,
        cellClass: 'content-center'
    },
    rowClassRules: {
        'ag-row-selected': params => {
            return params.data?.uuid == selectedUuid.value
        }
    },
    cellSelection: false,
    rowSelection: {
        mode: 'singleRow',
        enableClickSelection: true,
        checkboxes: false
    },
    onCellFocused: (event: CellFocusedEvent) => {
        if (event.rowIndex === null) return
        const rowNode = event.api.getDisplayedRowAtIndex(event.rowIndex)
        setRowNode(rowNode)
    },
    onRowDoubleClicked: (event: RowDoubleClickedEvent<Assignment>) => {
        if (!event.data) return
        setRowNode(event.node)
        detailsVisible.value = true
    },
    suppressContextMenu: true,
    enableCellTextSelection: true,
    context: {
        openAssignmentDetails: (assignmentUuid: string) => {
            const rowNode = gridApi.value?.getRowNode(assignmentUuid)
            setRowNode(rowNode)
            detailsVisible.value = true
        },
        openEditAssignmentPopup: (assignmentUuid: string) => {
            selectedUuid.value = assignmentUuid
            const node = gridApi.value?.getRowNode(assignmentUuid)
            editAssignmentModal.open({
                assignment: node?.data,
                onSaved: (updated: Assignment) => {
                    const rowNode = gridApi.value?.getRowNode(updated.uuid)
                    if (rowNode) rowNode.setData(updated)
                }
            })
        }
    },
}

// TODO complete button
// TODO a sensible way to show the data in the links
// TODO attach links to new or existing transactions
const colDefs: ColDef<Assignment>[] = [
    {
        headerName: '#',
        field: 'assignmentNo',
        filter: 'agNumberColumnFilter',
        sort: 'desc',
        sortable: true,
        cellClass: 'text-center content-center',
        maxWidth: 100,
        flex: 1,
        minWidth: 30,
    },
    {
        headerName: 'Oppgave',
        field: 'title',
        filter: 'agTextColumnFilter',
        sortable: true,
        flex: 3,
        minWidth: 70,
    },
    {
        headerName: 'Kategori',
        cellRenderer: CategoryCellRenderer,
        field: 'type',
        flex: 2,
    },
    {
        headerName: 'Koblinger',
        cellRenderer: ResourceLinksCellRenderer,
        field: 'resourceLinks',
        flex: 2,
    },
    {
        headerName: 'Status',
        cellClass: 'text-center content-center',
        cellRenderer: StatusCellRenderer,
        maxWidth: 100
    },
    {
        headerName: 'Handlinger',
        width: 160,
        suppressHeaderMenuButton: true,
        cellRendererSelector: (params: ICellRendererParams<Assignment>) => {
            const actionable = params.data?.context
            if (!actionable) return undefined
            const component = resolveCellRenderer(actionable)
            return {component, ...params}
        }
    }
]

const createAssignments = async (newFiles: DocumentMeta[]) => {
    const promises = []
    for (const file of newFiles) {
        promises.push(createIncomingDocumentAssignment(file.uuid))
    }
    await Promise.all(promises)
    if (gridApi.value) gridApi.value?.refreshServerSide()
}

const handleChangeStatus = (value: any) => {
    selectedStatusFilter.value = value
    if (gridApi.value) {
        gridApi.value.refreshServerSide({purge: true})
    }
}

const handleChangeCategory = (value: any) => {
    selectedCategoryFilter.value = value
    if (gridApi.value) {
        gridApi.value.refreshServerSide({purge: true})
    }
}

const handleChangeSearchTerm = (value: string) => {
    searchTerm.value = value
    if (gridApi.value) {
        gridApi.value.refreshServerSide({purge: true})
    }
}

const setRowNode = (rowNode: IRowNode<Assignment> | undefined) => {
    if (!rowNode || !rowNode.data) return
    gridApi.value?.setNodesSelected({nodes: [rowNode], newValue: true})
    selectedUuid.value = rowNode.data.uuid
}

useEventListener(window, 'mousemove', (e) => {
    if (!isResizing.value) return
    const newWidth = containerRight.value - e.clientX

    const minWidth = detailsMinWidth
    const maxWidth = containerTotalWidth.value * 0.9

    if (newWidth > minWidth && newWidth < maxWidth) {
        detailsWidth.value = newWidth
    }
})

useEventListener(window, 'mouseup', () => {
    isResizing.value = false
})

onMounted(() => {
    onEvents([...ASSIGNMENTS_CHANGED_EVENTS, ORG_SELECTED, ORG_SWITCHED], () => {
        if (gridApi.value) gridApi.value?.refreshServerSide()
    })
})

onUnmounted(cleanUp)

</script>

<style>
@reference "@/index.css";
@reference "tailwindcss";

.assignment-list-view {
    #assignment-list {
        .ag-cell:focus-within {
            border-color: transparent;
        }

        .ag-header {
            z-index: 0;
        }
    }
}
</style>
